/**
 * Date Grouping Utility
 *
 * Provides utilities for grouping items by date ranges.
 *
 * Usage:
 *   import { groupItemsByDate } from '@/utils/dateGrouping.js'
 *
 *   const grouped = groupItemsByDate(items, 'updated_at')
 *   // Returns: { today, yesterday, thisWeek, older }
 */

import dayjs from "dayjs";

/**
 * Group items by date ranges
 * @param {Array} items - Array of objects with a date property
 * @param {string} dateField - Name of the date field (default: 'updated_at')
 * @returns {Object} - { today, yesterday, thisWeek, older }
 */
export function groupItemsByDate(items, dateField = "updated_at") {
  const now = dayjs();

  const today = items.filter((item) =>
    dayjs(item[dateField]).isSame(now, "day")
  );

  const yesterday = items.filter((item) =>
    dayjs(item[dateField]).isSame(now.subtract(1, "day"), "day")
  );

  const thisWeek = items.filter((item) => {
    const itemDate = dayjs(item[dateField]);
    return (
      itemDate.isBefore(now.subtract(1, "day").startOf("day")) &&
      itemDate.isAfter(now.subtract(7, "day"))
    );
  });

  const older = items.filter((item) =>
    dayjs(item[dateField]).isBefore(now.subtract(7, "day"))
  );

  return {
    today,
    yesterday,
    thisWeek,
    older,
  };
}

/**
 * Check if a date is today
 * @param {string|Date} date - Date to check
 * @returns {boolean} - True if date is today
 */
export function isToday(date) {
  return dayjs(date).isSame(dayjs(), "day");
}

/**
 * Check if a date is yesterday
 * @param {string|Date} date - Date to check
 * @returns {boolean} - True if date is yesterday
 */
export function isYesterday(date) {
  return dayjs(date).isSame(dayjs().subtract(1, "day"), "day");
}

/**
 * Check if a date is within this week (excluding today and yesterday)
 * @param {string|Date} date - Date to check
 * @returns {boolean} - True if date is this week
 */
export function isThisWeek(date) {
  const itemDate = dayjs(date);
  const now = dayjs();
  return (
    itemDate.isBefore(now.subtract(1, "day").startOf("day")) &&
    itemDate.isAfter(now.subtract(7, "day"))
  );
}

/**
 * Check if a date is older than a week
 * @param {string|Date} date - Date to check
 * @returns {boolean} - True if date is older than a week
 */
export function isOlderThanWeek(date) {
  return dayjs(date).isBefore(dayjs().subtract(7, "day"));
}
